export type CanalExperiencia = "WEB_BETA" | "WEB_OFICIAL" | "EXTENSAO_BETA" | "EXTENSAO_OFICIAL";

export const RECURSOS = [
  "inicio", "clientes", "acompanhamento", "followUp", "carteira", "crm", "vendas",
  "numerosOperacionais", "whatsapp", "biblioteca", "pipeline", "pipelineGestao",
  "pipelineComentarios", "configuracoes", "gestao", "codigosSv",
  "vistoria", "ferramenta", "downloads", "modoSistemaConfiguravel",
  "metricasMensais", "etiquetas", "integracoes", "leads", "analise", "anuncios",
] as const;

export type RecursoProduto = typeof RECURSOS[number];
export type RecursosProduto = Record<RecursoProduto, boolean>;
export type VarianteClassificacao = "BP_AUTOMATICA" | "MANUAL";
export type VariantePipeline = "BP_GERENCIADA" | "INDIVIDUAL" | "OCULTA";
export type EtiquetaProduto = {
  id?: string;
  valor: string;
  label: string;
  cor?: string;
  prioridade?: number;
  ativa?: boolean;
  papelEstrutural?: "FRIO" | "PAGO" | "PERDIDO" | null;
};

export type ExperienciaProduto = {
  produto: "FOLLOW_ZAP" | "FOLLOW_PRO";
  implementacao: string | null;
  canal: CanalExperiencia;
  marca: { nome: string; sigla: string };
  recursos: RecursosProduto;
  classificacao: VarianteClassificacao;
  pipeline: VariantePipeline;
  etiquetas: readonly EtiquetaProduto[];
};

const ETIQUETAS_FOLLOW_ZAP = [
  { valor: "FECHAMENTO", label: "Em fechamento" },
  { valor: "QUENTE", label: "Quente" },
  { valor: "MORNO", label: "Morno" },
  { valor: "SEM_RESPOSTA", label: "Sem resposta" },
  { valor: "FRIO", label: "Frio" },
  { valor: "PAGO", label: "Pago" },
  { valor: "PERDIDO", label: "Perdido" },
] as const satisfies readonly EtiquetaProduto[];

function recursos(ativos: readonly RecursoProduto[]): RecursosProduto {
  const conjunto = new Set(ativos);
  return Object.fromEntries(RECURSOS.map((recurso) => [recurso, conjunto.has(recurso)])) as RecursosProduto;
}

const RECURSOS_NUCLEO_COMPARTILHADO: readonly RecursoProduto[] = [
  "inicio", "clientes", "acompanhamento", "followUp", "carteira", "crm", "vendas",
  "numerosOperacionais", "whatsapp", "biblioteca", "configuracoes",
];
const RECURSOS_POR_PRODUTO: Record<ExperienciaProduto["produto"], readonly RecursoProduto[]> = {
  FOLLOW_ZAP: ["metricasMensais", "etiquetas", "integracoes", "leads", "analise", "anuncios"],
  FOLLOW_PRO: ["modoSistemaConfiguravel"],
};
const RECURSOS_FOLLOW_PRO_WEB: readonly RecursoProduto[] = ["analise", "configuracoes", "vendas", "etiquetas"];

function recursosDaExperiencia(produto: ExperienciaProduto["produto"], canal: CanalExperiencia) {
  // O Driver do FOLLOW_PRO continua protegido por produto, papel e concessão individual.
  const nucleo = produto === "FOLLOW_PRO" ? [] : RECURSOS_NUCLEO_COMPARTILHADO;
  const followProWeb = produto === "FOLLOW_PRO" && (canal === "WEB_BETA" || canal === "WEB_OFICIAL") ? RECURSOS_FOLLOW_PRO_WEB : [];
  return recursos([...nucleo, ...RECURSOS_POR_PRODUTO[produto], ...followProWeb]);
}

export function canalExperiencia(request?: Request): CanalExperiencia {
  const informado = request?.headers.get("x-bp-canal");
  if (informado === "EXTENSAO_BETA" || informado === "EXTENSAO_OFICIAL") return informado;
  return process.env.NEXT_PUBLIC_BP_WEB_BETA === "1" ? "WEB_BETA" : "WEB_OFICIAL";
}

export function resolverExperienciaProduto(
  produto: ExperienciaProduto["produto"],
  implementacao: string | null,
  canal: CanalExperiencia,
): ExperienciaProduto {
  if (produto === "FOLLOW_ZAP") {
    return {
      produto, implementacao, canal,
      marca: { nome: "FollowZap", sigla: "FZ" },
      recursos: recursosDaExperiencia(produto, canal),
      classificacao: "MANUAL",
      pipeline: "OCULTA",
      etiquetas: ETIQUETAS_FOLLOW_ZAP,
    };
  }

  return {
    produto, implementacao, canal,
    marca: { nome: "FollowPro", sigla: "FP" },
    recursos: recursosDaExperiencia(produto, canal),
    classificacao: "MANUAL",
    pipeline: "OCULTA",
    etiquetas: [],
  };
}

export function recursoDisponivel(experiencia: Pick<ExperienciaProduto, "recursos">, recurso: RecursoProduto) {
  return experiencia.recursos[recurso] === true;
}

export function produtoEPapelPermitemAnalise(produto: ExperienciaProduto["produto"], papel: string, canal: CanalExperiencia) {
  return (produto === "FOLLOW_ZAP" && (papel === "USUARIO" || papel === "GESTOR")) ||
    (produto === "FOLLOW_PRO" && papel === "GESTOR" && (canal === "WEB_BETA" || canal === "WEB_OFICIAL"));
}

export function etiquetaDisponivel(experiencia: Pick<ExperienciaProduto, "etiquetas">, valor: string) {
  return experiencia.etiquetas.some((etiqueta) => etiqueta.valor === valor);
}


